import { supabase } from '../lib/supabase';

export type AgeVerificationStatus = 'unverified' | 'pending' | 'verified' | 'rejected';

export interface AgeVerificationRequestPayload {
  documentType: string;
  documentCountry: string;
  documentNumberLast4?: string;
  dateOfBirth: string;
  file: File;
}

export const ageVerificationService = {
  async getCurrentStatus() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('Usuario no autenticado');
    }

    const { data, error } = await supabase
      .from('profiles')
      .select('age_verified, age_verification_status')
      .eq('id', user.id)
      .maybeSingle();

    if (error) {
      throw error;
    }

    return {
      age_verified: data?.age_verified ?? false,
      age_verification_status: (data?.age_verification_status as AgeVerificationStatus) ?? 'unverified',
    };
  },

  async startVerification(payload: AgeVerificationRequestPayload) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('Usuario no autenticado');
    }

    const bucket = 'age-verification-docs';
    const fileExt = payload.file.name.split('.').pop();
    const fileName = `doc-${Date.now()}.${fileExt}`;
    const filePath = `${user.id}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(filePath, payload.file);

    if (uploadError) {
      throw uploadError;
    }

    const { data: verification, error: insertError } = await supabase
      .from('age_verifications')
      .insert({
        user_id: user.id,
        status: 'pending',
        document_type: payload.documentType,
        document_country: payload.documentCountry,
        document_number_last4: payload.documentNumberLast4 || null,
        date_of_birth: payload.dateOfBirth,
        provider: null,
        provider_reference: null,
        fraud_score: null,
        metadata: null,
        document_storage_path: filePath,
      })
      .select()
      .single();

    if (insertError) {
      throw insertError;
    }

    const { error: profileError } = await supabase
      .from('profiles')
      .update({
        age_verification_status: 'pending',
        updated_at: new Date().toISOString(),
      })
      .eq('id', user.id);

    if (profileError) {
      throw profileError;
    }

    return verification;
  },

  /**
   * Inicia la sesión de verificación con Didit llamando al backend privado.
   */
  async startDiditVerification(backendUrl: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || !user.email) {
      throw new Error('Usuario no autenticado o sin email');
    }

    try {
      // Llamada al endpoint del backend privado que crea la sesión
      const response = await fetch(`${backendUrl}/api/age-verification/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.id,
          email: user.email
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Error al conectar con el servidor de verificación');
      }

      const data = await response.json();
      
      // Actualizamos estado local a 'pending' mientras el usuario verifica
      // (Opcional, ya que el webhook lo hará, pero mejora la UX inmediata)
      await supabase
        .from('profiles')
        .update({
          age_verification_status: 'pending',
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      return {
        url: data.url,
        sessionId: data.session_id
      };
    } catch (error) {
      console.error('Error starting Didit verification:', error);
      throw error;
    }
  }
};

